import React from 'react';
import { Card, Button, Icon } from './ui';

/*
 * ErrorBoundary — catches render/runtime errors from the subtree it wraps and
 * shows a clean fallback (a Card with an icon + message + Reload) instead of
 * white-screening the whole app.
 *
 * Wrap the *content area* (the mounted screen), NOT the whole app, so a crashing
 * screen (e.g. the current EventsTable) shows the fallback while the shell chrome
 * (sidebar + header) stays visible and usable.
 *
 * Reset behavior: if `resetKey` changes (e.g. the selected screen changes) the
 * boundary clears its error state so navigating away from a crashed screen
 * recovers without a full page reload.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log for diagnostics; there is no error-reporting service wired here.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught error in screen:', error, info);
  }

  componentDidUpdate(prevProps) {
    // Clear the error when the wrapped screen changes so navigation recovers.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasError: false, error: null });
    }
  }

  handleReload() {
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="gw-fallback">
          <Card pad className="gw-fallback__card">
            <div className="gw-fallback__icon">
              <Icon name="warning" title="Error" />
            </div>
            <div className="gw-fallback__title">
              {this.props.title || 'Something went wrong on this screen'}
            </div>
            <div className="gw-fallback__msg">
              {this.props.message ||
                'This view hit an unexpected error and could not render. The rest of the console is still available from the sidebar, or you can reload the app.'}
            </div>
            <div className="gw-fallback__actions">
              <Button variant="primary" icon={<Icon name="refresh" size={15} />} onClick={this.handleReload}>
                Reload
              </Button>
            </div>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
